import React, { useCallback, useEffect } from 'react';

import ANALYTICS_CATEGORIES from '../../../analytics/categories';
import type { Account } from '../../../generated-types';
import useShowPaymentResult from '../../../hooks/useShowPaymentResult';
import useTranslation from '../../../hooks/useTranslation';
import { ANALYTICS_END, ANALYTICS_START, AnalyticsEvents, trackEvent } from '../../../services/analytics';
import { SigningContextProvider, useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { EikaModal } from '../../EikaModal';
import { MultiformWrapper } from '../MultiformWrapper';
import { PaymentAndTransferForm } from '../PaymentAndTransferForm';
import { TransferActionType } from './form/actions';
import transferReducer from './form/reducer';
import { transferInitialState } from './form/state';
import useFormContext, { TransferFormContext } from './form/useFormContext';
import useValidate from './form/validate';

import '../TransactionModal.scss';

export interface TransferProps {
    show: boolean;
    setShow: React.Dispatch<boolean>;
    onClose: (openPayment: boolean) => void;
    account?: Account;
}

const Transfer: React.FC<TransferProps> = ({ show, setShow, onClose, account }) => {
    const translated = useTranslation();
    const { handleChange } = useFormContext();

    const { signingStatus, dynamicLinkId, showSigningFlow, dispatch, handleCancelSigningFlow } = useSigningContext();

    const onCloseTransfer = useCallback(
        (openPayment = false) => {
            if (signingStatus === 'PROCESSING' && dynamicLinkId) {
                handleCancelSigningFlow();
            }
            handleChange({
                type: TransferActionType.RESET,
            });
            dispatch({ type: Actions.RESET });
            onClose(openPayment);
        },
        [handleChange, onClose, dispatch, dynamicLinkId, handleCancelSigningFlow, signingStatus],
    );

    useEffect(() => {
        if (show) {
            trackEvent(AnalyticsEvents.TRANSFER, ANALYTICS_CATEGORIES.TRANSFER, ANALYTICS_START);
        } else {
            trackEvent(AnalyticsEvents.TRANSFER, ANALYTICS_CATEGORIES.TRANSFER, ANALYTICS_END);
        }
    }, [show]);

    const showPaymentResult = useShowPaymentResult();
    const hideCloseButton = showSigningFlow && !showPaymentResult;

    return (
        <EikaModal
            id="transfer"
            className="transaction-modal"
            open={show}
            setOpen={setShow}
            ariaLabel={translated.transfer}
            onClose={onCloseTransfer}
            timeOut={signingStatus === 'COMPLETED' ? 2500 : undefined}
            contentShouldStretch
            hideCloseButton={hideCloseButton}
            size="wide"
            desktopMinHeight="small"
        >
            <PaymentAndTransferForm
                defaultFocusActive
                defaultForm="transfer"
                account={account}
                wrapInContext={['payment']}
                transferForm={<MultiformWrapper transactionType="transfer" account={account} type="modal" />}
                paymentForm={<MultiformWrapper transactionType="payment" account={account} type="modal" />}
            />
        </EikaModal>
    );
};

const TransferFormController: React.FC<TransferProps> = ({ show, setShow, onClose, account }) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.TRANSFER}>
        <TransferFormContext
            validateHook={useValidate}
            reducer={transferReducer}
            initialState={{ ...transferInitialState }}
        >
            <Transfer show={show} setShow={setShow} onClose={onClose} account={account} />
        </TransferFormContext>
    </SigningContextProvider>
);

export default TransferFormController;
